/**
 * ImportHistoryPanel - Shows past import operations with stats
 * PLATFORM:F1.P9-10 Phase 2: Import history tracking
 */
import React, { useState, useEffect } from 'react';
import { Clock, Upload, FileText, CheckCircle, XCircle, AlertCircle, RefreshCw, RotateCcw, Loader2, Ban, Timer } from 'lucide-react';
import { getImportHistory, rollbackImport } from '../../services/vaultApi';

const STATUS_CONFIG = {
  completed: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/10', label: 'Completed' },
  failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', label: 'Failed' },
  processing: { icon: RefreshCw, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Processing' },
  pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', label: 'Pending' },
  rolled_back: { icon: AlertCircle, color: 'text-zinc-400', bg: 'bg-zinc-500/10', label: 'Rolled Back' },
  // V-IMPORT: a previewed import the user cancelled, or one whose preview TTL lapsed. Neither wrote
  // a row; neither is 'pending' (which used to be the silent default for any unknown status).
  cancelled: { icon: Ban, color: 'text-zinc-400', bg: 'bg-zinc-500/10', label: 'Cancelled' },
  expired: { icon: Timer, color: 'text-orange-400', bg: 'bg-orange-500/10', label: 'Expired' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Served (import_source, import_format) pairs of CollectionImportLog: ('clz','clz'),
 * ('csv','csv') and the bulk barcode preview/confirm's ('barcode','barcode_txt').
 */
function formatSource(source, format) {
  if (format === 'clz' || source === 'clz') return 'CLZ Import';
  if (format === 'csv' || source === 'csv') return 'CSV Import';
  if (format === 'barcode_txt' || source === 'barcode') return 'Bulk Barcode Import';
  return source || 'Import';
}

export default function ImportHistoryPanel({ isOpen, onClose }) {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rollbackConfirm, setRollbackConfirm] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getImportHistory(20);
      // ImportHistoryResponse is {imports, total}; a body without the list renders as none.
      setImports(data.imports || []);
    } catch (err) {
      setError(err.message || 'Failed to load import history');
    }
    setLoading(false);
  };

  const handleRollback = async (importId) => {
    setRollbackLoading(true);
    try {
      await rollbackImport(importId);
      setRollbackConfirm(null);
      await loadHistory();
    } catch (err) {
      setError(err.message || 'Rollback failed');
    } finally {
      setRollbackLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="import-history-panel">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-400" />
            <h2 className="text-lg font-bold text-white">Import History</h2>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-white text-sm"
            aria-label="Close import history"
          >
            Close
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-zinc-700 border-t-orange-500 rounded-full" />
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-red-400 text-sm">{error}</p>
              <button onClick={loadHistory} className="text-orange-400 text-sm mt-2 hover:underline">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && imports.length === 0 && (
            <div className="text-center py-8">
              <Upload className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400 text-sm">No imports yet</p>
              <p className="text-zinc-500 text-xs mt-1">
                Import a CLZ or CSV file to get started.
              </p>
            </div>
          )}

          {!loading && !error && imports.length > 0 && (
            <div className="space-y-3">
              {imports.map((imp) => {
                const statusCfg = STATUS_CONFIG[imp.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusCfg.icon;

                return (
                  <div
                    key={imp.id}
                    className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-800"
                    data-testid={`import-history-item-${imp.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-zinc-500" />
                        <div>
                          <p className="text-sm font-medium text-white">
                            {imp.file_name || 'Unknown file'}
                          </p>
                          <p className="text-xs text-zinc-500">
                            {formatSource(imp.import_source, imp.import_format)}
                            {' \u00B7 '}
                            {formatDate(imp.created_at)}
                          </p>
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${statusCfg.bg} ${statusCfg.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusCfg.label}
                      </span>
                    </div>

                    {/* Stats row */}
                    <div className="flex gap-4 text-xs">
                      <span className="text-zinc-400">
                        <span className="text-green-400 font-medium">{imp.rows_imported || 0}</span> imported
                      </span>
                      {(imp.rows_matched > 0) && (
                        <span className="text-zinc-400">
                          <span className="text-blue-400 font-medium">{imp.rows_matched}</span> matched
                        </span>
                      )}
                      {(imp.rows_skipped > 0) && (
                        <span className="text-zinc-400">
                          <span className="text-yellow-400 font-medium">{imp.rows_skipped}</span> skipped
                        </span>
                      )}
                      {(imp.rows_failed > 0) && (
                        <span className="text-zinc-400">
                          <span className="text-red-400 font-medium">{imp.rows_failed}</span> failed
                        </span>
                      )}
                    </div>

                    {/* Rollback action */}
                    {imp.status === 'completed' && (
                      <div className="mt-2 pt-2 border-t border-zinc-800">
                        {rollbackConfirm === imp.id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleRollback(imp.id)}
                              disabled={rollbackLoading}
                              className="flex-1 flex items-center justify-center gap-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg py-1.5 text-xs transition-colors disabled:opacity-50"
                              data-testid={`rollback-confirm-${imp.id}`}
                            >
                              {rollbackLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                              Confirm Rollback
                            </button>
                            <button
                              onClick={() => setRollbackConfirm(null)}
                              className="px-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg py-1.5 text-xs transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setRollbackConfirm(imp.id)}
                            className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
                            data-testid={`rollback-${imp.id}`}
                          >
                            <RotateCcw className="w-3 h-3" />
                            Rollback Import
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
